import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const empty = { couple_names: "", location: "", quote: "", photo_url: "", wedding_date: "", template_used: "", rating: 5, visible: true, sort_order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data);
    } catch {
      toast.error("Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.couple_names.trim() || !form.quote.trim()) return toast.error("Couple names and quote are required");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/testimonials/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/testimonials", form);
        toast.success("Added");
      }
      setForm(empty); setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const edit = (t) => { setEditing(t.id); setForm({ ...empty, ...t }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const pickPhoto = () => fileRef.current?.click();
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Max 6MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: `${BACKEND_URL}${r.data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const fld = "w-full min-h-[44px] px-3 py-2 bg-white border border-stone-300 outline-none focus:border-stone-900 transition-colors text-sm";

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-testimonials-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-10">
        <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-stone-500 hover:text-stone-900">← Back to admin</Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-2">Testimonials</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light tracking-tight">Real customer reviews</h1>
            <div className="text-sm text-stone-500 mt-2">Add couples' photos + quotes — they appear on the Landing page testimonials carousel.</div>
          </div>
          <div className="text-sm text-stone-600">{items.length} total · {items.filter(i => i.visible).length} live</div>
        </div>

        {/* FORM */}
        <form onSubmit={save} className="bg-white border border-stone-200 p-6 mb-10 grid md:grid-cols-2 gap-4" data-testid="testimonial-form">
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="font-serif-display text-2xl">{editing ? "Edit testimonial" : "Add a testimonial"}</div>
            {editing && <button type="button" onClick={cancel} className="text-xs uppercase tracking-[0.2em] text-stone-500 underline underline-offset-4">Cancel edit</button>}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Couple names *</label>
            <input data-testid="t-couple" required value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} placeholder="Riya & Karthik" className={fld} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Location</label>
            <input data-testid="t-location" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bengaluru · India" className={fld} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Their quote *</label>
            <textarea data-testid="t-quote" required rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="Every guest asked us where we got it..." className={fld + " min-h-[110px]"} maxLength={600} />
            <div className="text-[10px] text-stone-400 mt-1">{form.quote.length}/600</div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Wedding date</label>
            <input data-testid="t-date" value={form.wedding_date || ""} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} placeholder="12 March 2026" className={fld} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Template they used</label>
            <input data-testid="t-template" value={form.template_used || ""} onChange={(e) => setForm({ ...form, template_used: e.target.value })} placeholder="Royal Indian / Editorial / etc" className={fld} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Rating</label>
            <select data-testid="t-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className={fld}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)} ({n})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Sort order (lower = first)</label>
            <input data-testid="t-sort" type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className={fld} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Couple photo</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
            {form.photo_url ? (
              <div className="flex items-start gap-3">
                <img src={form.photo_url} alt="" className="w-32 h-40 object-cover border border-stone-200" />
                <div className="space-y-2">
                  <button type="button" onClick={pickPhoto} disabled={uploading} className="px-4 py-2 border border-stone-300 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 block">{uploading ? "Uploading…" : "Replace photo"}</button>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))} className="px-4 py-2 border border-red-300 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-50 block">Remove</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={pickPhoto} disabled={uploading} className="w-full py-4 border border-dashed border-stone-400 text-xs uppercase tracking-[0.3em] text-stone-600 hover:bg-stone-50">{uploading ? "Uploading…" : "+ Upload couple photo"}</button>
            )}
          </div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm">
            <input data-testid="t-visible" type="checkbox" checked={form.visible !== false} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="w-4 h-4 accent-stone-900" />
            Visible on Landing page
          </label>
          <div className="md:col-span-2">
            <button type="submit" disabled={busy || uploading} data-testid="t-save" className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.3em] hover:bg-stone-800 disabled:opacity-60">
              {busy ? "Saving…" : editing ? "Update Testimonial" : "+ Add Testimonial"}
            </button>
          </div>
        </form>

        {/* LIST */}
        {items.length === 0 ? (
          <div className="border border-dashed border-stone-300 p-16 text-center">
            <div className="font-serif-display text-2xl mb-2">No testimonials yet.</div>
            <div className="text-stone-500 text-sm">Add your first one above — it will appear on the homepage immediately.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((t) => (
              <article key={t.id} className={`bg-white border p-5 ${t.visible ? "border-stone-200" : "border-stone-300 opacity-60"}`} data-testid={`testimonial-${t.id}`}>
                {t.photo_url && <img src={t.photo_url} alt="" className="w-full aspect-[4/5] object-cover mb-3 bg-stone-100" />}
                <div className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: "#B89B5E" }}>{"★".repeat(t.rating || 5)}</div>
                <p className="text-sm text-stone-700 italic leading-relaxed line-clamp-4">"{t.quote}"</p>
                <div className="font-serif-display text-xl mt-3">{t.couple_names}</div>
                <div className="text-xs text-stone-500">{t.location}{t.wedding_date && ` · ${t.wedding_date}`}{t.template_used && ` · ${t.template_used}`}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => edit(t)} data-testid={`t-edit-${t.id}`} className="px-3 py-1.5 border border-stone-900 text-stone-900 text-[10px] uppercase tracking-[0.2em] hover:bg-stone-100">Edit</button>
                  <button onClick={() => del(t.id)} data-testid={`t-del-${t.id}`} className="px-3 py-1.5 border border-red-300 text-red-700 text-[10px] uppercase tracking-[0.2em] hover:bg-red-50">Delete</button>
                  {!t.visible && <span className="px-2 py-1 bg-stone-100 text-stone-600 text-[10px] uppercase tracking-[0.2em]">Hidden</span>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
